import React, { useEffect, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Truck, LogIn, Clock, Cash, CreditCard, Barcode } from 'iconoir-react';
import { formatPrice } from '../utils/currency';
import useComandaPolling from '../hooks/useComandaPolling';
import Lottie from 'lottie-react';
import pedidoEnviadoAnimation from '../assets/pedido-enviado-cardapio.json';
import deliveryEmptyAnimation from '../assets/delivery-empty.json';
import cashbackAnimation from '../assets/cashback-grub.json';
import metaAtingidaAnimation from '../assets/meta-atingida.json';
import deliveryEntregueAnimation from '../assets/delivery-entregue.json';
import deliveryPedidoCanceladoAnimation from '../assets/pedido-cancelado.json';
import pedidoEmPreparoAnimation from '../assets/em-preparo.json';
import PedidoAcaminho from '../assets/enviando-delivery.json';
import animationDeliver from '../assets/animation-deliver.json';
import foodDelivered from '../assets/Food delivered.json';

function PedidoDetalhesLayout({ comandaData, showLoginPrompt = false, onLoginClick, onComandaUpdate }) {
  const [pedidoCanceladoAnim, setPedidoCanceladoAnim] = useState(null);
  const [currentComandaData, setCurrentComandaData] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Polling para atualizar status da comanda
  useComandaPolling(comandaData?.id, (comandaAtualizada) => {
    setCurrentComandaData(comandaAtualizada);
    if (onComandaUpdate) {
      onComandaUpdate(comandaAtualizada);
    }
  }, true);

  // Atualizar dados quando props mudam (com delay mínimo de 0.5s)
  useEffect(() => {
    if (!comandaData) {
      setCurrentComandaData(null);
      return;
    }
    const timer = setTimeout(() => {
      setCurrentComandaData(comandaData);
    }, 500);
    return () => clearTimeout(timer);
  }, [comandaData]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let mounted = true;
    const tryLoad = async () => {
      const paths = [
        (process.env.PUBLIC_URL || '') + '/pedido-cancelado.json',
        (process.env.PUBLIC_URL || '') + '/assets/pedido-cancelado.json'
      ];
      for (const p of paths) {
        try {
          const res = await fetch(p);
          if (res.ok) {
            const json = await res.json();
            if (mounted) setPedidoCanceladoAnim(json);
            break;
          }
        } catch (e) {
          // silent
        }
      }
    };
    tryLoad();
    return () => { mounted = false; };
  }, []);

  const normalizeStatus = (statusRaw) => {
    const s = (statusRaw || '').toLowerCase();
    const map = {
      'em_preparo': 'producao',
      'preparando': 'producao',
      'em_producao': 'producao',
      'aberto': 'aberta',
      'pronto': 'pronta',
      'pronto_para_retirada': 'pronta',
      'retirado': 'fechada',
      'entregue': 'fechada',
      'finalizada': 'fechada',
      'cancelado': 'cancelada',
      'cancelada': 'cancelada',
      'aguardando_pagamento': 'aguardando_pagamento',
      'pagamento_pendente': 'aguardando_pagamento',
      'pagamento_confirmado': 'pagamento_confirmado',
      'em_entrega': 'caminho',
      'saiu_para_entrega': 'caminho',
      'a_caminho': 'caminho',
      'a-caminho': 'caminho',
      'a caminho': 'caminho',
      'acaminho': 'caminho',
      'em_transito': 'caminho',
      'em trânsito': 'caminho',
      'em_rota': 'caminho',
      'em rota': 'caminho'
    };
    return map[s] || s;
  };

  const STATUS_CONFIG = {
    'aberta': {
      badgeText: 'ANÁLISE',
      badgeClass: 'bg-info',
      meta: { bg: '#fdeccdff', title: 'Recebemos seu pedido', subtitle: 'Estamos organizando tudo para começar o preparo.' },
      animation: animationDeliver
    },
    'producao': {
      badgeText: 'PREPARO',
      badgeClass: 'bg-warning text-dark',
      meta: { bg: '#ddecff', title: 'Seu pedido está em preparo', subtitle: 'Nossa equipe está preparando seu pedido com carinho.' },
      animation: pedidoEmPreparoAnimation
    },
    'pronta': {
      badgeText: 'PRONTO',
      badgeClass: 'bg-success',
      meta: { bg: '#ddecff', title: 'Seu pedido esta pronto', subtitle: 'Estamos finalizando os últimos detalhes.' },
      animation: foodDelivered
    },
    'fechada': {
      badgeText: 'ENTREGUE',
      badgeClass: 'bg-success',
      meta: { bg: '#c0e8c9', title: 'Seu pedido foi entregue', subtitle: 'Obrigado pela preferência e bom apetite.' },
      animation: deliveryEntregueAnimation
    },
    'caminho': {
      badgeText: 'A CAMINHO',
      badgeClass: 'bg-primary',
      meta: { bg: '#ddecff', title: 'Pedido à caminho', subtitle: 'O entregador está a caminho.' },
      animation: PedidoAcaminho
    },
    'aguardando_pagamento': {
      badgeText: 'PROCESSANDO',
      badgeClass: 'bg-warning text-dark',
      meta: { bg: '#fef3c7', title: 'Aguardando pagamento', subtitle: 'Finalize o pagamento para iniciarmos o preparo.' },
      animation: deliveryEmptyAnimation
    },
    'pagamento_confirmado': {
      badgeText: 'PAGO',
      badgeClass: 'bg-success',
      meta: { bg: '#ddecff', title: 'Pagamento confirmado', subtitle: 'Estamos liberando seu pedido para preparo.' },
      animation: metaAtingidaAnimation
    },
    'cancelada': {
      badgeText: 'CANCELADO',
      badgeClass: 'bg-danger',
      meta: { bg: '#fdd8d8ff', title: 'Pedido cancelado', subtitle: 'Este pedido foi cancelado, se tiver dúvidas entre em contato com o estabelecimento.' },
      animation: deliveryPedidoCanceladoAnimation
    }
  };

  const getStatusAnimation = (status) => {
    const key = normalizeStatus(status);
    if (key === 'cancelada' && pedidoCanceladoAnim) return pedidoCanceladoAnim;
    return STATUS_CONFIG[key]?.animation || deliveryEmptyAnimation;
  };

  const getStatusMeta = (status) => {
    const key = normalizeStatus(status);
    return STATUS_CONFIG[key]?.meta || { bg: '#f3f4f6', title: 'Seu pedido está em análise ou em processamento.', subtitle: 'Acompanhe as atualizações do seu pedido por aqui.' };
  };

  const renderTimeline = (comandaData) => {
    const key = normalizeStatus(comandaData.status);
    const stage = (key === 'fechada') ? 3 : (key === 'caminho' ? 2 : (key === 'producao' || key === 'pronta' ? 1 : 0));
    const isMesa = !comandaData.b_delivery && (comandaData.tipo === 'mesa' || comandaData.mesa?.b_comanda === 0);
    const grupos = key === 'cancelada'
      ? (isMesa
          ? [
              { id: 'analise', label: 'Análise', active: false },
              { id: 'preparo', label: 'Preparo', active: false },
              { id: 'final', label: 'Cancelado', active: true, danger: true }
            ]
          : [
              { id: 'analise', label: 'Análise', active: false },
              { id: 'preparo', label: 'Preparo', active: false },
              { id: 'caminho', label: 'Caminho', active: false },
              { id: 'final', label: 'Cancelado', active: true, danger: true }
            ])
      : (isMesa
          ? [
              { id: 'analise', label: 'Análise', active: stage >= 0 },
              { id: 'preparo', label: 'Preparo', active: stage >= 1 },
              { id: 'final', label: 'Entregue', active: stage >= 3 }
            ]
          : [
              { id: 'analise', label: 'Análise', active: stage >= 0 },
              { id: 'preparo', label: 'Preparo', active: stage >= 1 },
              { id: 'caminho', label: 'Caminho', active: stage >= 2 },
              { id: 'final', label: 'Entregue', active: stage >= 3 }
            ]);
    const previsaoDireta = comandaData.entrega?.previsao || (comandaData.entrega?.previsao_inicio && comandaData.entrega?.previsao_fim ? `${comandaData.entrega.previsao_inicio} - ${comandaData.entrega.previsao_fim}` : null);
    let previsao = previsaoDireta;
    if (!previsao) {
      const tempo = comandaData.entrega?.tempoEntrega || comandaData.entrega?.tempo || comandaData.endereco?.entrega?.tempoEntrega || 30;
      const baseStr = comandaData.updated_at || comandaData.created_at;
      const baseDate = baseStr ? new Date(baseStr) : new Date();
      if (!isNaN(baseDate.getTime())) {
        const inicio = new Date(baseDate.getTime() + Math.max(tempo - 10, 0) * 60000);
        const fim = new Date(baseDate.getTime() + tempo * 60000);
        const fmt = (d) => d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        previsao = `${fmt(inicio)} - ${fmt(fim)}`;
      }
    }
    return (
      <div className="p-3 mb-3" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow:'0 1px 2px rgba(0,0,0,0.04)', marginTop:'10px'}}>
        <div
          style={{
            display:'grid',
            gridTemplateColumns: `repeat(${grupos.length}, 1fr)`,
            columnGap:'8px',
            alignItems:'start'
          }}
        >
          {grupos.map((g) => (
            <div key={g.id} style={{minWidth:0, textAlign:'left'}}>
              <div style={{height:'2px', width:'100%', borderRadius:'9999px', backgroundColor: g.active ? (g.danger ? '#ef4444' : 'rgb(16, 185, 129)') : '#e5e7eb', margin:'0 0 8px 0'}} />
              <div style={{fontSize:'0.85rem', fontWeight: g.active ? 700 : 500, color: g.active ? (g.danger ? '#ef4444' : '#111827') : '#6b7280', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{g.label}</div>
            </div>
          ))}
        </div>
        {(previsao && stage < 2) && (
          <div style={{marginTop:'10px', fontSize:'0.85rem', color:'#6b7280', textAlign:'left'}}>
            Previsão de entrega: <span style={{color:'#111827', fontWeight:600}}>{previsao}</span>
          </div>
        )}
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Data inválida';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Data inválida';
    return date.toLocaleDateString('pt-BR') + ' - ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatGroup = (arr, showValue = false) => {
    if (!Array.isArray(arr) || arr.length === 0) return '';
    const map = {};
    for (const a of arr) {
      const nome = a?.nome || '';
      const valor = parseFloat(a?.valor || 0) || 0;
      if (!map[nome]) map[nome] = { qtd: 0, valor };
      map[nome].qtd += 1;
    }
    const parts = Object.entries(map).map(([nome, info]) => {
      const mult = info.qtd > 1 ? ` x${info.qtd}` : '';
      const extra = showValue && info.valor ? ` (+${formatPrice(info.valor * info.qtd)})` : '';
      return `${nome}${mult}${extra}`;
    });
    return parts.join(', ');
  };

  const getPaymentIcon = (pagamento) => {
    const tipo = (pagamento?.tipo || '').toLowerCase();
    if (tipo.includes('pix')) return <Barcode />;
    if (tipo.includes('dinheiro') || tipo.includes('cash')) return <Cash />;
    return <CreditCard />;
  };

  if (!currentComandaData) {
    return (
      <div>
        {/* Skeleton Header */}
        <div className="p-3 mb-3" style={{backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.04)'}}>
          <div className="d-flex align-items-center" style={{background: '#f3f4f6', borderRadius: '12px', padding: '12px'}}>
            <div style={{width: '96px', height: '96px', flexShrink: 0, background: '#e5e7eb', borderRadius: '8px', animation: 'pulse 1.5s ease-in-out infinite'}} />
            <div className="flex-grow-1" style={{marginLeft: '12px'}}>
              <div style={{height: '20px', width: '70%', background: '#e5e7eb', borderRadius: '4px', marginBottom: '8px', animation: 'pulse 1.5s ease-in-out infinite'}} />
              <div style={{height: '16px', width: '90%', background: '#e5e7eb', borderRadius: '4px', animation: 'pulse 1.5s ease-in-out infinite'}} />
            </div>
          </div>
          <div className="p-3 mb-3" style={{marginTop: '10px'}}>
            <div style={{height: '2px', width: '100%', background: '#e5e7eb', borderRadius: '9999px', marginBottom: '8px', animation: 'pulse 1.5s ease-in-out infinite'}} />
          </div>
        </div>

        {/* Skeleton Produtos */}
        <div className="mb-4">
          <div style={{height: '18px', width: '150px', background: '#e5e7eb', borderRadius: '4px', marginBottom: '12px', animation: 'pulse 1.5s ease-in-out infinite'}} />
          <div className="p-3" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)'}}>
            {[1, 2, 3].map((i) => (
              <div key={i} className="d-flex align-items-center py-2" style={{borderBottom: i !== 3 ? '1px solid #e5e7eb' : 'none', gap:'12px'}}>
                <div style={{width:'50px', height:'50px', borderRadius:'8px', background: '#e5e7eb', animation: 'pulse 1.5s ease-in-out infinite'}} />
                <div className="flex-grow-1">
                  <div style={{height: '16px', width: '60%', background: '#e5e7eb', borderRadius: '4px', marginBottom: '6px', animation: 'pulse 1.5s ease-in-out infinite'}} />
                  <div style={{height: '14px', width: '40%', background: '#e5e7eb', borderRadius: '4px', animation: 'pulse 1.5s ease-in-out infinite'}} />
                </div>
                <div style={{height: '16px', width: '60px', background: '#e5e7eb', borderRadius: '4px', animation: 'pulse 1.5s ease-in-out infinite'}} />
              </div>
            ))}
          </div>
        </div>

        {/* Skeleton Entrega */}
        <div className="mb-4">
          <div style={{height: '18px', width: '180px', background: '#e5e7eb', borderRadius: '4px', marginBottom: '12px', animation: 'pulse 1.5s ease-in-out infinite'}} />
          <div className="p-3" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)'}}>
            <div className="d-flex align-items-center" style={{gap:'12px'}}>
              <div style={{width:'40px', height:'40px', borderRadius:'8px', background: '#e5e7eb', animation: 'pulse 1.5s ease-in-out infinite'}} />
              <div className="flex-grow-1">
                <div style={{height: '16px', width: '50%', background: '#e5e7eb', borderRadius: '4px', marginBottom: '6px', animation: 'pulse 1.5s ease-in-out infinite'}} />
                <div style={{height: '14px', width: '70%', background: '#e5e7eb', borderRadius: '4px', animation: 'pulse 1.5s ease-in-out infinite'}} />
              </div>
            </div>
          </div>
        </div>

        {/* Skeleton Resumo */}
        <div className="mb-4">
          <div style={{height: '18px', width: '160px', background: '#e5e7eb', borderRadius: '4px', marginBottom: '12px', animation: 'pulse 1.5s ease-in-out infinite'}} />
          <div className="p-3" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)'}}>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="d-flex justify-content-between mb-2">
                <div style={{height: '14px', width: '100px', background: '#e5e7eb', borderRadius: '4px', animation: 'pulse 1.5s ease-in-out infinite'}} />
                <div style={{height: '14px', width: '80px', background: '#e5e7eb', borderRadius: '4px', animation: 'pulse 1.5s ease-in-out infinite'}} />
              </div>
            ))}
          </div>
        </div>

        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </div>
    );
  }

  const todosProdutos = currentComandaData.pedidos?.flatMap(pedido => 
    pedido.produtos?.map(produto => ({
      ...produto,
      pedido_id: pedido.id
    })) || []
  ) || [];

  // Usar valores da API (calculados no backend)
  const valores = currentComandaData.valores || {
    produtos: todosProdutos.reduce((total, produto) => {
      return total + (parseFloat(produto.valor) * parseInt(produto.quantidade));
    }, 0),
    taxa_servico: 0,
    taxa_entrega: 0,
    total: 0,
    pago: currentComandaData.pagamentos?.reduce((total, pagamento) => {
      return total + parseFloat(pagamento.valor || 0);
    }, 0) || 0,
    faltante: 0,
    desconto: 0
  };
  
  // Se não veio da API, calcular manualmente (fallback)
  if (!currentComandaData.valores) {
    valores.total = valores.produtos + valores.taxa_servico + valores.taxa_entrega - valores.desconto;
    valores.faltante = Math.max(0, valores.total - valores.pago);
  }
  

  const entregaInfo = Array.isArray(currentComandaData.entrega) ? currentComandaData.entrega[0] : currentComandaData.entrega;
  // Usar valor do backend (valores.taxa_entrega) como fonte de verdade - pode ser 0 para frete grátis
  const taxaEntregaValue = currentComandaData.valores ? valores.taxa_entrega : (entregaInfo?.taxa_entrega || entregaInfo?.zonaEntrega?.valor || 0);

  return (
    <>
      {/* Header com status */}
      <div className="p-3 mb-3" style={{backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.04)'}}>
        {(() => {
          const meta = getStatusMeta(currentComandaData.status);
          return (
            <div>
              <div className="d-flex align-items-center" style={{background: meta.bg, borderRadius: '12px', padding: '12px'}}>
                <div style={{width: '96px', height: '96px', flexShrink: 0, display:'flex', alignItems:'center', justifyContent:'center'}}>
                  <Lottie animationData={getStatusAnimation(currentComandaData.status)} loop={true} style={{width:'100%', height:'100%'}} />
                </div>
                <div className="flex-grow-1" style={{marginLeft: '12px'}}>
                  <div style={{fontWeight: '700', fontSize: '1rem', color: '#111827'}}>{meta.title}</div>
                  <div style={{color: '#6b7280', fontSize: '0.85rem'}}>{meta.subtitle}</div>
                </div>
              </div>
              {renderTimeline(currentComandaData)}
              <div className="mt-2" style={{textAlign: 'left'}}>
                <span style={{color: '#9ca3af', fontSize: '0.85rem'}}>
                  <span className="pulsing-dot"></span>
                  Última atualização: {formatDate(currentComandaData.updated_at)}
                </span>
              </div>
            </div>
          );
        })()}
      </div>

      {normalizeStatus(currentComandaData.status) === 'aberta' && (
        <div className="p-3 mb-3" style={{backgroundColor:'#ffffffff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow:'0 1px 2px rgba(0,0,0,0.04)'}}>
          <h6 className="mb-2" style={{fontSize:'0.85rem', fontWeight:'700', color:'#ff6100'}}>Pedido em Análise</h6>
          <p className="mb-0" style={{color:'#6b7280', fontSize:'0.9rem'}}>
            Recebemos seu pedido e estamos verificando todos os detalhes. Em breve iniciaremos o preparo.
          </p>
        </div>
      )}

      {normalizeStatus(currentComandaData.status) === 'em_entrega' && (
        <div className="p-3 mb-3" style={{backgroundColor:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow:'0 1px 2px rgba(0,0,0,0.04)'}}>
          <h6 className="mb-2" style={{fontSize:'0.85rem', fontWeight:'700', color:'#1a1a1a'}}>A caminho</h6>
          <p className="mb-0" style={{color:'#6b7280', fontSize:'0.9rem'}}>
            Seu pedido está a caminho. O entregador está se dirigindo ao endereço informado.
          </p>
        </div>
      )}

      {/* Lista de Produtos */}
      <div className="mb-4">
        <h6 className="mb-2" style={{fontSize: isMobile ? '0.75rem' : '0.85rem', fontWeight: '600', color: '#1a1a1a', textAlign: 'left'}}>
          Itens do Pedido ({todosProdutos.length})
        </h6>
        {todosProdutos.length > 0 ? (
          <div className="p-3" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)'}}>
            {todosProdutos.map((produto, index) => (
              <div key={index} className="d-flex align-items-center py-2" style={{borderBottom: index !== todosProdutos.length-1 ? '1px solid #e5e7eb' : 'none', gap:'12px'}}>
                <img 
                  src={produto.imagem ? `https://grubupload.s3.amazonaws.com/${produto.imagem}` : 'https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg'}
                  alt={produto.nome}
                  style={{width:'50px', height:'50px', borderRadius:'8px', objectFit:'cover'}}
                  onError={(e) => { e.target.src = 'https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg'; }}
                />
                <div className="flex-grow-1">
                  <div className="fw-bold" style={{fontSize: isMobile ? '0.75rem' : '0.85rem', color:'#111827'}}>{produto.nome}</div>
                  {produto.b_cortesia ? (
                    <div style={{color:'#059669', fontSize: isMobile ? '0.75rem' : '0.85rem', fontWeight: 500}}>🎁 Grátis • Fidelidade</div>
                  ) : (
                    <div style={{color:'#6b7280', fontSize: isMobile ? '0.75rem' : '0.85rem'}}>{produto.quantidade} x {formatPrice(produto.valor)}</div>
                  )}
                  {(produto.adicionais?.length > 0 || produto.opcionais?.length > 0) && (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id={`info-prod-${produto.pedido_id || index}`}>
                          <div style={{textAlign:'left'}}>
                            {produto.adicionais?.length > 0 && (
                              <div><strong>Adicionais:</strong> {formatGroup(produto.adicionais, true)}</div>
                            )}
                            {produto.opcionais?.length > 0 && (
                              <div><strong>Opcionais:</strong> {formatGroup(produto.opcionais, false)}</div>
                            )}
                          </div>
                        </Tooltip>
                      }
                    >
                      <span className="badge" style={{background:'#eef2ff', color:'#1f2937', border:'1px solid #c7d2fe', fontSize:'0.75rem'}}>+ info do pedido</span>
                    </OverlayTrigger>
                  )}
                </div>
                <div className="text-end">
                  {produto.b_cortesia ? (
                    <div className="fw-bold" style={{fontSize:'0.85rem', color:'#059669'}}>Grátis</div>
                  ) : (
                    <div className="fw-bold" style={{fontSize:'0.9rem', color:'#111827'}}>{formatPrice(parseFloat(produto.valor) * parseInt(produto.quantidade))}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-muted text-center py-3">Nenhum produto encontrado</div>
        )}
      </div>

      <div className="mb-4">
        <h6 className="mb-2" style={{fontSize: '0.85rem', fontWeight: '700', color: '#1a1a1a', textAlign: 'left'}}>
          Informações de Entrega
        </h6>
        <div className="p-3 d-flex align-items-center justify-content-between" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)'}}>
          <div className="d-flex align-items-center" style={{gap:'12px'}}>
            <div style={{width:'40px', height:'40px', borderRadius:'8px', background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid #e5e7eb'}}>
              <Truck style={{color:'#6b7280'}} />
            </div>
            <div>
              <div className="fw-bold" style={{fontSize:'0.9rem', color:'#111827'}}>
                {entregaInfo ? 'Endereço de entrega' : (currentComandaData.b_balcao ? 'Retirada / Balcão' : (currentComandaData.mesa ? 'Entrega em' : 'Retirada / Balcão'))}
              </div>
              <div className="text-muted" style={{fontSize:'0.8rem'}}>
                {entregaInfo
                  ? `${entregaInfo.rua || ''}${entregaInfo.numero > 0 ? ' - ' + entregaInfo.numero : ''}${entregaInfo.bairro ? ', ' + entregaInfo.bairro : ''}`
                  : currentComandaData.b_balcao
                    ? (
                      <>
                        Entrega interna ou retirada
                        {(currentComandaData.nome_cliente && currentComandaData.nome_cliente !== '0' && currentComandaData.nome_cliente !== 0) && (
                          <div style={{marginTop:'4px'}}><strong>Cliente:</strong> {currentComandaData.nome_cliente}</div>
                        )}
                        {(currentComandaData.mesa_cliente && currentComandaData.mesa_cliente !== '0' && currentComandaData.mesa_cliente !== 0) && (
                          <div><strong>Mesa:</strong> {currentComandaData.mesa_cliente}</div>
                        )}
                      </>
                    )
                    : currentComandaData.mesa
                      ? `Mesa ${currentComandaData.mesa.numero || currentComandaData.mesa.identificador || currentComandaData.identificador}` 
                      : 'Entrega interna ou retirada'}
              </div>
            </div>
          </div>
          <div className="text-end">
            <div className="fw-bold" style={{fontSize:'0.85rem'}}>
              {entregaInfo ? formatPrice(parseFloat(taxaEntregaValue)) : (currentComandaData.b_balcao ? 'Balcão' : (currentComandaData.mesa ? 'Garçom' : '-'))}
            </div>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <h6 className="mb-2" style={{fontSize:'0.85rem', fontWeight:'600', color:'#1a1a1a', textAlign: 'left'}}>
          Resumo Financeiro
        </h6>
        <div className="p-3" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow:'0 1px 2px rgba(0,0,0,0.04)'}}>
          {currentComandaData.pagamentos && currentComandaData.pagamentos.length > 0 && (
            <>
              <h6 className="mb-3" style={{fontSize:'0.85rem', fontWeight:'600', color:'#1a1a1a'}}>Pagamentos ({currentComandaData.pagamentos.length})</h6>
              {currentComandaData.pagamentos.map((pagamento, index) => {
                const formaPag = pagamento.formaPagamento || {};
                const bandeira = formaPag.bandeira || '';
                const tipo = formaPag.tipo || pagamento.tipo || '';
                const troco = parseFloat(pagamento.troco || 0);
                const isPix = (bandeira.toLowerCase().includes('pix') || tipo.toLowerCase().includes('pix'));
                const iconUrl = isPix 
                  ? 'https://grubicons.s3.us-east-1.amazonaws.com/pix.svg'
                  : (formaPag.icon ? `https://grubicons.s3.us-east-1.amazonaws.com${formaPag.icon}` : null);
                
                return (
                  <div key={index} className="d-flex align-items-center justify-content-between p-3 mb-2" style={{backgroundColor:'#fff', borderRadius:'12px', border:'1px solid #e9ecef'}}>
                    <div className="d-flex align-items-center flex-grow-1" style={{gap:'12px'}}>
                      <div style={{width:'40px', height:'40px', borderRadius:'8px', background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid #e5e7eb', padding:'4px'}}>
                        <img 
                          src={iconUrl || 'https://grubicons.s3.us-east-1.amazonaws.com/logo-grub-isologo.svg'} 
                          alt={bandeira || 'Pagamento'} 
                          style={{width:'100%', height:'100%', objectFit:'contain'}} 
                        />
                      </div>
                      <div className="flex-grow-1">
                        <div style={{fontWeight:'600', fontSize:'0.9rem', color:'#1a1a1a', marginBottom:'4px', textTransform:'uppercase'}}>
                          {bandeira && tipo ? `${bandeira} - ${tipo}` : (bandeira || tipo || 'Pagamento')}
                        </div>
                        <div style={{color:'#6b7280', fontSize:'0.8rem'}}>
                          {formatDate(pagamento.created_at)}
                        </div>
                        {troco > 0 && (
                          <div style={{color:'#6b7280', fontSize:'0.8rem', marginTop:'2px'}}>
                            <strong style={{color:'#1a1a1a'}}>Troco:</strong> {formatPrice(troco)}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-end">
                      <div style={{fontWeight:'700', fontSize:'0.85rem', color:'#10b981', marginBottom:'6px'}}>
                        {formatPrice(pagamento.valor)}
                      </div>
                      <div className="small">
                        {currentComandaData.pago ? (
                          <span className="badge" style={{backgroundColor:'#10b981', fontSize:'0.7rem'}}>PAGO</span>
                        ) : (
                          <span className="badge" style={{backgroundColor:'#c7c7c7ff', fontSize:'0.7rem'}}>PENDENTE</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <hr className="my-3" />
            </>
          )}
          <div className="d-flex justify-content-between mb-2" style={{fontSize:'0.85rem', color:'#6b7280'}}>
            <span>Produtos:</span>
            <span className="fw-bold" style={{color:'#1a1a1a'}}>{formatPrice(valores.produtos)}</span>
          </div>
          {valores.desconto > 0 && (
            <div className="d-flex justify-content-between mb-2" style={{fontSize:'0.85rem', color:'#10b981'}}>
              <span>Desconto:</span>
              <span className="fw-bold" style={{color:'#10b981'}}>-{formatPrice(valores.desconto)}</span>
            </div>
          )}
          {(!currentComandaData?.b_delivery) && (
            <div className="d-flex justify-content-between mb-2" style={{fontSize:'0.85rem', color:'#6b7280'}}>
              <span>Taxa de Serviço:</span>
              <span className="fw-bold" style={{color:'#1a1a1a'}}>{formatPrice(valores.taxa_servico)}</span>
            </div>
          )}
          {valores.taxa_entrega > 0 && (
            <div className="d-flex justify-content-between mb-2" style={{fontSize:'0.85rem', color:'#6b7280'}}>
              <span>Taxa de Entrega:</span>
              <span className="fw-bold" style={{color:'#1a1a1a'}}>{formatPrice(valores.taxa_entrega)}</span>
            </div>
          )}
          <div className="d-flex justify-content-between mt-2" style={{fontSize:'0.85rem'}}>
            <span className="fw-bold" style={{color:'#1a1a1a'}}>Total:</span>
            <span className="fw-bold" style={{fontSize:'1.1rem', color:'#10b981'}}>{formatPrice(valores.total)}</span>
          </div>
          {valores.faltante > 0 && (
            <div className="d-flex justify-content-between mt-2" style={{fontSize:'0.85rem', color:'#f59e0b'}}>
              <span>Valor Pendente:</span>
              <span className="fw-bold">{formatPrice(valores.faltante)}</span>
            </div>
          )}
          {showLoginPrompt && (
            <div className="mt-3 text-center" style={{
              background: '#eaf2ff',
              border: '1px solid #cfe0ff',
              borderRadius: '16px',
              padding: '20px'
            }}>
              <div style={{width: '200px', height: '200px', margin: '0 auto'}}>
                <Lottie 
                  animationData={cashbackAnimation} 
                  loop={true}
                  style={{width: '100%', height: '100%'}}
                />
              </div>
              <div style={{fontSize: '1rem', fontWeight: 700, color: '#0f172a', marginTop: '8px'}}>Ganhe CASHBACK<br />na próxima compra.</div>

              <div style={{color: '#6b7280', fontSize: '0.9rem'}}>No Clube de Benefícios GRUB, <br /> você sempre sai ganhando.</div>
              <button 
                className="btn btn-primary mt-4"
                style={{marginTop: '12px', padding: '10px 16px', borderRadius: '10px', fontWeight: 600, fontSize: '0.85rem'}}
                onClick={onLoginClick}
              >
                <LogIn className="me-1" />
                Cadastre-se
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default PedidoDetalhesLayout;
